<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { Close } from '@element-plus/icons-vue'
import { useKnowledgeFloatStore } from '../stores/knowledgeFloat'
import { useWorkspaceStore } from '../stores/workspace'
import { useDirectoryStore } from '../stores/directory'
import { useDocumentStore } from '../stores/document'
import { NodeType } from '../types'
import WorkspaceSelector from './WorkspaceSelector.vue'
import DirectoryPanel from './DirectoryPanel.vue'
import DocumentEditor from './DocumentEditor.vue'
import PaneTabBar from './PaneTabBar.vue'

const floatStore = useKnowledgeFloatStore()
const workspaceStore = useWorkspaceStore()
const directoryStore = useDirectoryStore()
const documentStore = useDocumentStore()

const MIN_WIDTH = 480
const MAX_WIDTH = 1200
const MIN_HEIGHT = 360
const MOBILE_BREAKPOINT = 768

const panelRef = ref(null)
const isDragging = ref(false)
const isResizing = ref(false)
const resizeDirection = ref('')
const dragStart = ref({ x: 0, y: 0 })
const startPos = ref({ x: 0, y: 0 })
const startSize = ref({ width: 0, height: 0 })

// 当前文档内容
const contentHtml = ref('')

const isMobile = computed(() =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
)

const currentWorkspaceId = computed(() => workspaceStore.currentWorkspaceId)

const activeTab = computed(() =>
  floatStore.openTabs.find(t => t.tabId === floatStore.activeTabId) || null
)

// 面板样式
const panelStyle = computed(() => {
  if (isMobile.value) {
    return { left: '0', top: '0', width: '100%', height: '100%', borderRadius: '0' }
  }
  return {
    left: `${floatStore.panelPosition.x}px`,
    top: `${floatStore.panelPosition.y}px`,
    width: `${floatStore.panelSize.width}px`,
    height: `${floatStore.panelSize.height}px`
  }
})

// ---- 加载工作空间 ----
const loadWorkspace = async (wsId) => {
  if (!wsId) return
  await directoryStore.fetchTree(wsId)
}

watch(currentWorkspaceId, (wsId) => {
  if (wsId) loadWorkspace(wsId)
})

onMounted(() => {
  floatStore.loadPanelLayout()
  document.addEventListener('keydown', handleKeyDown)
  if (currentWorkspaceId.value) {
    loadWorkspace(currentWorkspaceId.value)
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handleHeaderMouseMove)
  document.removeEventListener('mouseup', handleHeaderMouseUp)
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
  document.removeEventListener('keydown', handleKeyDown)
})

// ---- 文档操作 ----

// activeTab 变化时加载文档
watch(() => activeTab.value?.documentId, async (docId) => {
  if (!docId) {
    contentHtml.value = ''
    return
  }
  const doc = await documentStore.fetchDocument(docId)
  if (doc) {
    contentHtml.value = doc.content_html || ''
  }
}, { immediate: true })

const handleOpenDocument = async (node) => {
  if (node.type !== NodeType.DOCUMENT) return
  const doc = await documentStore.fetchDocumentByNodeId(node.node_id)
  if (!doc) return
  floatStore.openDocument({
    documentId: doc.document_id,
    nodeId: node.node_id,
    title: doc.title || node.name
  })
}

const handleSave = async (html) => {
  if (activeTab.value?.documentId) {
    await documentStore.saveDocument(activeTab.value.documentId, html)
  }
}

const handleWorkspaceSwitch = (wsId) => {
  loadWorkspace(wsId)
}

// ---- 拖拽标题栏 ----
const handleHeaderMouseDown = (e) => {
  if (isMobile.value) return
  if (e.target.closest('.panel-controls')) return
  if (e.button !== 0) return

  isDragging.value = true
  dragStart.value = { x: e.clientX, y: e.clientY }
  startPos.value = { ...floatStore.panelPosition }

  document.addEventListener('mousemove', handleHeaderMouseMove)
  document.addEventListener('mouseup', handleHeaderMouseUp)
}

const handleHeaderMouseMove = (e) => {
  if (!isDragging.value) return
  const deltaX = e.clientX - dragStart.value.x
  const deltaY = e.clientY - dragStart.value.y

  let newX = startPos.value.x + deltaX
  let newY = startPos.value.y + deltaY
  newX = Math.max(0, Math.min(window.innerWidth - floatStore.panelSize.width, newX))
  newY = Math.max(0, Math.min(window.innerHeight - 48, newY))

  floatStore.setPanelPosition({ x: newX, y: newY })
}

const handleHeaderMouseUp = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', handleHeaderMouseMove)
  document.removeEventListener('mouseup', handleHeaderMouseUp)
}

// ---- 调整大小 ----
const handleResizeStart = (direction, e) => {
  if (isMobile.value) return
  if (e.button !== 0) return

  isResizing.value = true
  resizeDirection.value = direction
  dragStart.value = { x: e.clientX, y: e.clientY }
  startPos.value = { ...floatStore.panelPosition }
  startSize.value = { ...floatStore.panelSize }

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
}

const handleResizeMove = (e) => {
  if (!isResizing.value) return

  const deltaX = e.clientX - dragStart.value.x
  const deltaY = e.clientY - dragStart.value.y

  let newWidth = startSize.value.width
  let newHeight = startSize.value.height
  let newX = startPos.value.x
  let newY = startPos.value.y

  if (resizeDirection.value.includes('e')) {
    newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startSize.value.width + deltaX))
  }
  if (resizeDirection.value.includes('s')) {
    newHeight = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - floatStore.panelPosition.y - 60, startSize.value.height + deltaY))
  }
  if (resizeDirection.value.includes('w')) {
    newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startSize.value.width - deltaX))
    newX = startPos.value.x + (startSize.value.width - newWidth)
  }
  if (resizeDirection.value.includes('n')) {
    newHeight = Math.max(MIN_HEIGHT, startSize.value.height - deltaY)
    newY = startPos.value.y + (startSize.value.height - newHeight)
  }

  floatStore.setPanelPosition({ x: newX, y: newY })
  floatStore.setPanelSize({ width: newWidth, height: newHeight })
}

const handleResizeEnd = () => {
  isResizing.value = false
  resizeDirection.value = ''
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
}

// ---- 关闭 ----
const handleClose = () => {
  floatStore.closePanel()
}

const handleKeyDown = (e) => {
  if (e.key === 'Escape' && floatStore.isExpanded) {
    floatStore.closePanel()
  }
}
</script>

<template>
  <div
    ref="panelRef"
    class="kb-float-panel"
    :style="panelStyle"
    role="dialog"
    aria-label="知识库面板"
  >
    <!-- 调整大小手柄 -->
    <div class="resize-handle resize-handle-n" @mousedown="handleResizeStart('n', $event)" />

    <!-- 标题栏 -->
    <div
      class="panel-header"
      :style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
      @mousedown="handleHeaderMouseDown"
    >
      <div class="drag-indicator">
        <span></span><span></span><span></span>
      </div>
      <div class="panel-title">
        <span>📚</span>
        <span>知识库</span>
      </div>
      <div class="panel-controls">
        <button
          class="ctrl-btn"
          :title="floatStore.sidebarVisible ? '隐藏目录' : '显示目录'"
          @click="floatStore.toggleSidebar()"
        >{{ floatStore.sidebarVisible ? '◀' : '☰' }}</button>
        <el-button text circle size="small" @click="handleClose">
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </div>

    <!-- 主体 -->
    <div class="panel-body">
      <!-- 左侧目录 -->
      <div
        class="panel-sidebar"
        :class="{ hidden: !floatStore.sidebarVisible }"
      >
        <div class="sidebar-ws">
          <WorkspaceSelector @switch="handleWorkspaceSwitch" />
        </div>
        <div class="sidebar-tree">
          <DirectoryPanel
            v-if="currentWorkspaceId"
            :workspace-id="currentWorkspaceId"
            @open-document="handleOpenDocument"
          />
        </div>
      </div>

      <!-- 分隔线 -->
      <div v-if="floatStore.sidebarVisible" class="panel-divider" />

      <!-- 右侧编辑器 -->
      <div class="panel-editor">
        <!-- 页签栏 -->
        <PaneTabBar
          :tabs="floatStore.openTabs"
          :active-tab-id="floatStore.activeTabId"
          pane-id="float"
          :droppable="false"
          @switch="floatStore.switchTab"
          @close="floatStore.closeTab"
        />

        <!-- 编辑器内容 -->
        <div class="editor-area">
          <DocumentEditor
            v-if="activeTab"
            :key="activeTab.documentId"
            v-model="contentHtml"
            :editable="true"
            @save="handleSave"
          />
          <div v-else class="editor-empty">
            <div class="empty-icon">📄</div>
            <div class="empty-text">从目录中选择文档打开</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 调整大小手柄 -->
    <div class="resize-handle resize-handle-s" @mousedown="handleResizeStart('s', $event)" />
    <div class="resize-handle resize-handle-e" @mousedown="handleResizeStart('e', $event)" />
    <div class="resize-handle resize-handle-w" @mousedown="handleResizeStart('w', $event)" />
  </div>
</template>

<style scoped>
.kb-float-panel {
  position: fixed;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

/* ---- 标题栏 ---- */
.panel-header {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  user-select: none;
  flex-shrink: 0;
}

.drag-indicator {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-right: 10px;
  padding: 4px;
}

.drag-indicator span {
  display: block;
  width: 16px;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  font-size: 15px;
  font-weight: 600;
}

.panel-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.panel-controls .el-button {
  color: white;
}

.panel-controls .el-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.ctrl-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.ctrl-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* ---- 主体 ---- */
.panel-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.panel-sidebar {
  width: 220px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.2s ease, min-width 0.2s ease, opacity 0.2s ease;
}

.panel-sidebar.hidden {
  width: 0;
  min-width: 0;
  opacity: 0;
  pointer-events: none;
}

.sidebar-ws {
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.sidebar-tree {
  flex: 1;
  overflow: hidden;
}

.panel-divider {
  width: 1px;
  background: #e5e7eb;
  flex-shrink: 0;
}

.panel-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.editor-area {
  flex: 1;
  overflow: hidden;
}

.editor-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  gap: 8px;
}

.empty-icon {
  font-size: 40px;
  opacity: 0.4;
}

.empty-text {
  font-size: 13px;
}

/* ---- 调整大小 ---- */
.resize-handle {
  position: absolute;
  z-index: 10;
}

.resize-handle-n,
.resize-handle-s {
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
}

.resize-handle-n { top: 0; }
.resize-handle-s { bottom: 0; }

.resize-handle-e,
.resize-handle-w {
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
}

.resize-handle-e { right: 0; }
.resize-handle-w { left: 0; }

.resize-handle:hover {
  background: rgba(14, 165, 233, 0.15);
}
</style>
